import { useState } from 'react';
import { useStoreActions, useStoreState } from '~/store';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '~/api';
import Helmet from 'Components/Helmet';
import Icon from 'Components/Icon';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { NavigationMenuItem } from '@/components/ui/navigation-menu';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import classNames from 'classnames';

interface MarketActivitySolution {
  id: string;
  icon: string;
  name: string;
  order_count: number;
  tender_count: number;
  user_has_access: boolean;
}

const SolutionCounts = ({
  order_count,
  tender_count,
}: Pick<MarketActivitySolution, 'order_count' | 'tender_count'>) => {
  if (order_count === 0 && tender_count === 0) return null;
  return (
    <div className="tw:text-xs tw:text-gray-400">
      {order_count > 0 && (
        <FormattedMessage
          id="solution-picker-order-count"
          defaultMessage="{count, plural, one {# order} other {# orders}}"
          values={{ count: order_count }}
        />
      )}
      {order_count > 0 && tender_count > 0 && (
        <span className="tw:mx-px">{' | '}</span>
      )}
      {tender_count > 0 && (
        <FormattedMessage
          id="solution-picker-tender-count"
          defaultMessage="{count, plural, one {# tender} other {# tenders}}"
          values={{ count: tender_count }}
        />
      )}
    </div>
  );
};

const SolutionPicker = () => {
  const location = useLocation();
  const isHome = location.pathname === '/home';

  const solution = useStoreState((state) => state.auth.solution);
  const changeSolution = useStoreActions(
    (action) => action.auth.changeSolution
  );
  const history = useHistory();
  const { formatMessage } = useIntl();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSolution, setSelectedSolution] =
    useState<MarketActivitySolution | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);

  const { data: marketActivity, isLoading } = useQuery<{
    solutions: MarketActivitySolution[];
  }>({
    queryKey: ['solutions', 'market-activity'],
    queryFn: () => api.getData2('/solutions/market-activity'),
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  });

  const solutions = marketActivity?.solutions || [];
  const accessibleSolutions = solutions.filter(
    (s: MarketActivitySolution) => s.user_has_access
  );

  const otherSolutions = solutions.filter(
    (s: MarketActivitySolution) => !s.user_has_access
  );

  const change = (id: string) => {
    changeSolution(id);
    history.push('/');
  };

  const registerSolution = async (solutionId: string) => {
    try {
      await api.postData('/user/register-solutions', [solutionId]);
      toast.success(
        formatMessage({
          id: 'solution-picker-register-success',
          defaultMessage: 'Registration successful',
        })
      );
    } catch {
      toast.error(
        formatMessage({
          id: 'solution-picker-register-error',
          defaultMessage: 'Registration failed',
        })
      );
    }
  };

  const handleRegisterClick = (s: MarketActivitySolution) => {
    setSelectedSolution(s);
    setDialogOpen(true);
  };

  const handleConfirmRegister = async () => {
    if (!selectedSolution || isRegistering) return;
    setIsRegistering(true);
    try {
      await registerSolution(selectedSolution.id);
      setDialogOpen(false);
      setSelectedSolution(null);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) setSelectedSolution(null);
  };

  if (isLoading) {
    return (
      <NavigationMenuItem>
        <Icon
          name="progress_activity"
          className="tw:align-middle tw:ml-2.5 tw:opacity-50 tw:text-base tw:animate-spin"
        />
      </NavigationMenuItem>
    );
  }

  return (
    <NavigationMenuItem>
      <Helmet>
        {solutions.map((s) => (
          <link key={s.id} rel="prefetch" as="image" href={s.icon} />
        ))}
      </Helmet>
      <DropdownMenu>
        <DropdownMenuTrigger className="tw:inline-flex tw:items-center tw:gap-1 tw:px-1 tw:py-1 tw:text-sm tw:rounded-md tw:outline-none tw:text-gray-500 tw:hover:bg-inherit tw:active:bg-inherit tw:focus:bg-inherit tw:focus:text-gray-500 tw:hover:text-primary">
          <FormattedMessage
            id="navbar-button-solutions"
            defaultMessage="Solutions"
          />
          <Icon name="arrow_drop_down" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {accessibleSolutions.map((s) => (
            <DropdownMenuItem
              key={s.id}
              onClick={() => change(s.id)}
              className={classNames(
                'tw:gap-3',
                s.id === solution?.id && !isHome && 'tw:bg-accent'
              )}
            >
              <img src={s.icon} alt={s.name} className="tw:w-8 tw:h-8" />
              <div className="tw:flex-1">
                {s.name}
                <SolutionCounts
                  order_count={s.order_count}
                  tender_count={s.tender_count}
                />
              </div>
            </DropdownMenuItem>
          ))}
          {otherSolutions.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>
                <FormattedMessage
                  id="solution-picker-register"
                  defaultMessage="Register"
                />
              </DropdownMenuLabel>
              {otherSolutions.map((s) => (
                <DropdownMenuItem
                  key={s.id}
                  onClick={() => handleRegisterClick(s)}
                  className="tw:gap-3 tw:group"
                >
                  <img src={s.icon} alt={s.name} className="tw:w-8 tw:h-8" />
                  <div className="tw:flex-1">
                    {s.name}
                    <SolutionCounts
                      order_count={s.order_count}
                      tender_count={s.tender_count}
                    />
                  </div>
                  <Icon
                    name="add"
                    className="tw:text-gray-500 tw:opacity-0 tw:group-hover:opacity-100"
                  />
                </DropdownMenuItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmDialog
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
        title={
          <FormattedMessage
            id="solution-picker-register-dialog-title"
            defaultMessage="Register for {name}"
            values={{ name: selectedSolution?.name }}
          />
        }
        confirmLabel={
          <FormattedMessage
            id="solution-picker-register-dialog-confirm"
            defaultMessage="Confirm"
          />
        }
        cancelLabel={
          <FormattedMessage
            id="solution-picker-register-dialog-cancel"
            defaultMessage="Cancel"
          />
        }
        onConfirm={handleConfirmRegister}
        isLoading={isRegistering}
      >
        <FormattedMessage
          id="solution-picker-register-dialog-description"
          defaultMessage="Would you like to register for this solution?"
        />
      </ConfirmDialog>
    </NavigationMenuItem>
  );
};

export default SolutionPicker;
